import { API_BASE } from '@/shared/services/api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, RefreshCw } from 'lucide-react';

const API_URL = API_BASE;
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('ezerpToken')}`
});

const statusColors = {
  pending: 'bg-amber-500/20 text-amber-400',
  running: 'bg-blue-500/20 text-blue-400',
  completed: 'bg-green-500/20 text-green-400',
  failed: 'bg-red-500/20 text-red-400'
};

export const StatutoryExportsPage = () => {
  const [definitions, setDefinitions] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countryPackFilter, setCountryPackFilter] = useState('all');
  const [periodCode, setPeriodCode] = useState('');

  const fetchExports = useCallback(async () => {
    setLoading(true);
    try {
      let definitionsUrl = `${API_URL}/api/accounting/exports/definitions`;
      if (countryPackFilter !== 'all') definitionsUrl += `?country_pack=${encodeURIComponent(countryPackFilter)}`;
      const [definitionsRes, jobsRes] = await Promise.all([
        fetch(definitionsUrl, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/accounting/exports/jobs`, { headers: getAuthHeaders() })
      ]);
      if (!definitionsRes.ok) throw new Error('Failed to fetch export definitions');
      if (!jobsRes.ok) throw new Error('Failed to fetch export jobs');
      const definitionsData = await definitionsRes.json();
      const jobsData = await jobsRes.json();
      setDefinitions(Array.isArray(definitionsData) ? definitionsData : definitionsData.definitions || []);
      setJobs(Array.isArray(jobsData) ? jobsData : jobsData.jobs || []);
    } catch (err) {
      toast.error(err.message);
      setDefinitions([]);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [countryPackFilter]);

  useEffect(() => {
    fetchExports();
  }, [fetchExports]);

  const countryPacks = useMemo(() => Array.from(new Set(definitions.map((item) => item.country_pack).filter(Boolean))).sort(), [definitions]);

  const createJob = async (definition) => {
    try {
      const params = new URLSearchParams({ export_code: definition.export_code });
      if (periodCode) params.set('period_code', periodCode);
      const res = await fetch(`${API_URL}/api/accounting/exports/jobs?${params.toString()}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create export job');
      }
      toast.success('Export job created');
      fetchExports();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="statutory-exports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">Statutory Exports</h1>
          <p className="text-muted-foreground mt-1">Generate statutory accounting exports from configured country packs.</p>
        </div>
        <Button variant="outline" onClick={fetchExports} className="gap-2">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div><p className="text-sm text-muted-foreground">Definitions</p><p className="text-3xl font-bold">{definitions.length}</p></div>
              <div className="p-3 rounded-lg bg-green-500/20"><FileSpreadsheet className="h-6 w-6 text-green-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Export Jobs</p>
            <p className="text-3xl font-bold">{jobs.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Completed Jobs</p>
            <p className="text-3xl font-bold">{jobs.filter((job) => job.status === 'completed').length}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            <CardTitle>Export Definitions</CardTitle>
            <div className="flex flex-col sm:flex-row gap-3">
              <Input placeholder="Period code, optional" value={periodCode} onChange={(e) => setPeriodCode(e.target.value)} className="w-[200px]" />
              <Select value={countryPackFilter} onValueChange={setCountryPackFilter}>
                <SelectTrigger className="w-[180px]"><SelectValue placeholder="Country pack" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Country Packs</SelectItem>
                  {countryPacks.map((pack) => (
                    <SelectItem key={pack} value={pack}>{pack}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
            </div>
          ) : definitions.length === 0 ? (
            <div className="text-center py-12">
              <Download className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">No export definitions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Code</th>
                    <th className="text-left p-3 font-medium">Name</th>
                    <th className="text-left p-3 font-medium">Country Pack</th>
                    <th className="text-left p-3 font-medium">Format</th>
                    <th className="text-right p-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {definitions.map((definition) => (
                    <tr key={definition.id || definition.export_code} className="hover:bg-slate-800/30">
                      <td className="p-3 font-mono text-sm">{definition.export_code || '-'}</td>
                      <td className="p-3 font-medium">{definition.name || definition.description || '-'}</td>
                      <td className="p-3"><Badge className="bg-blue-500/20 text-blue-400">{definition.country_pack || '-'}</Badge></td>
                      <td className="p-3">{definition.file_format || definition.format || '-'}</td>
                      <td className="p-3 text-right">
                        <Button size="sm" className="gap-2 bg-green-600 hover:bg-green-700" onClick={() => createJob(definition)}>
                          <Download className="h-4 w-4" /> Create Job
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Recent Export Jobs</CardTitle></CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No export jobs found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Job</th>
                    <th className="text-left p-3 font-medium">Export</th>
                    <th className="text-left p-3 font-medium">Period</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {jobs.map((job) => (
                    <tr key={job.id} className="hover:bg-slate-800/30">
                      <td className="p-3 font-mono text-sm">#{job.id}</td>
                      <td className="p-3">{job.export_code || '-'}</td>
                      <td className="p-3">{job.period_code || '-'}</td>
                      <td className="p-3"><Badge className={statusColors[job.status] || 'bg-gray-500/20 text-gray-400'}>{job.status || 'unknown'}</Badge></td>
                      <td className="p-3 text-sm">{job.created_at ? new Date(job.created_at).toLocaleString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StatutoryExportsPage;
